"use client";

import { useEffect, useId, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Link, useRouter } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import useSolopreneurProfile, { useEntrepriseChoisie, useHydrate, useIdentite } from "@/hooks/useSolopreneurProfile";
import { dossierVide } from "@/lib/tools/dossier";
import { alertesAffichees, situationDuDossier } from "@/lib/tools/dossierSituation";
import { dossierDepuisEntreprise } from "@/lib/tools/entreprise";
import { euros, valeurLisible } from "@/lib/tools/dossierTextes";
import { finAcre } from "@/lib/tools/incomeTax";
import { T as T_SITUATION, dateCourte, kickerAlerte, nJours, pourcent, remplir, texteAlerte } from "./situationTextes";
import { SOURCES_AMI } from "@/lib/tools/partage";
import { partagerSituation } from "./partagerSituation";

/**
 * L'entrée de « Votre situation », en haut de /outils.
 *
 * LE NOM DE L'ENTREPRISE AU LIEU DE SIX QUESTIONS. L'annuaire public de
 * l'État (app/api/tools/company-search) donne le statut, l'activité et la
 * date de début : il ne reste que micro ou réel, puis le chiffre d'affaires.
 * Le lecteur voit sa situation se calculer pendant qu'il fait glisser le
 * curseur, avant même d'avoir fini de répondre. C'est ce qui explique la
 * page mieux qu'un texte.
 *
 * DEUX PORTES DE MÊME RANG. « J'ai mon entreprise » et « Je me lance » sont
 * deux onglets, pas un lien caché sous la barre : celui qui n'a encore rien
 * déclaré est le premier public de la rubrique.
 *
 * UN SEUL OBJET QUI SE TRANSFORME. Pas de deuxième titre ni de carte dans la
 * carte : la barre de recherche, et sous elle l'exemple chiffré. Quand le
 * lecteur choisit son entreprise, la barre devient la fiche de l'entreprise
 * (même place, `layoutId`), et la fiche MONTRE ce que l'État sait déjà :
 * l'activité telle que l'Insee la nomme, l'âge de l'entreprise, le siège, le
 * numéro de TVA, les marques officielles. La question qui manque s'y pose,
 * puis le curseur, puis l'ACRE. La bande de l'exemple passe de Camille à
 * « Vous » au même moment, avec les dates qui l'attendent.
 *
 * QUATRE ÉTATS : « recherche » (la barre, et l'exemple dessous), « trouvee »
 * (ce que l'annuaire a dit, et ce qu'il ne dit pas), « curseur » (le chiffre
 * d'affaires, avec les trois chiffres en direct), et « situation » pour celui
 * qui a déjà un dossier (ses propres chiffres et le premier point à regarder).
 *
 * L'EXEMPLE SE CALCULE, IL NE S'ÉCRIT PAS. Camille, consultante en micro, est
 * passée dans les moteurs des outils à la date du jour : ses chiffres restent
 * justes quand un barème change. Le serveur et le navigateur le calculent
 * avec la même date (`aujourdhui`), le HTML est donc le même des deux côtés.
 */

const EXEMPLE = { statut: "micro", activite: "bnc", dateDebut: "2025-03-02", caCetteAnnee: 45200, caAnDernier: 31000, enCouple: false, enfants: 0, autresRevenus: 0 };

// Exporté avec Recherche, formeDe et communeLisible : le choix d'une entreprise
// est repris à l'onboarding et au tableau de bord (components/Tools/CompanyPicker.jsx).
export const T = {
    fr: {
        ongletEntreprise: "J'ai mon entreprise",
        ongletProjet: "Je me lance",
        appel: "On vous dit ce qui vous attend.",
        appelProjet: "Deux réponses, et c'est calculé.",
        bonjour: "Bonjour {prenom}.",
        trouvee: "Trouvée.",
        connu: "Voici ce que l'annuaire sait déjà.",
        champ: "Nom de l'entreprise ou SIRET",
        annuaire: "Annuaire public de l'État",
        recherche: "Recherche dans l'annuaire…",
        aucun: "Aucune entreprise active ne porte ce nom. Essayez votre SIRET, ou répondez aux questions.",
        occupe: "L'annuaire public ne répond pas pour l'instant. Réessayez dans un instant, ou répondez aux questions.",
        trouvees: "{n} entreprises trouvées dans l'annuaire public.",
        trouveeUne: "Une entreprise trouvée dans l'annuaire public.",
        rienGarde: "Rien n'est gardé tant que vous ne choisissez pas.",
        depuis: "depuis {date}",
        questions: "Répondre aux questions",
        sansRecherche: "Répondre aux questions à la place",
        vous: "Vous",
        direct: "Calculé en direct",
        parMois: "par mois pour elle",
        parMoisVous: "par mois pour vous",
        apres: "après l'Urssaf, l'impôt et la formation",
        tva: "Seuil de TVA",
        plafond: "Plafond micro",
        charges: "Cotisations et impôt",
        parAn: "par an",
        dansJours: "dans {jours}",
        passe: "passé le {date}",
        sousSeuil: "Non",
        sousSeuilDetail: "sous {base}",
        janvier: "1er janv.",
        janvierDetail: "l'an prochain",
        due: "Due",
        dueDetail: "depuis le 1er janvier",
        dePlafond: "de {plafond}",
        mystere: "Vos chiffres s'affichent ici",
        envoyerAmi: "Envoyer à un ami solopreneur",
        messageCopie: "Message copié, collez-le dans votre conversation",
        accueilAmi: "Un ami vous a envoyé cette page.",
        accueilAmiSuite: "Tapez le nom de votre entreprise : vous verrez vos propres dates, en quelques réponses.",
        tvaDeja: "Déjà facturée",
        tvaDejaDetail: "sur vos factures",
        tvaFacturee: "Je facture déjà la TVA",
        tvaFactureeDetail: "Par option, ou le seuil passé une année précédente : l'outil ne vous l'annoncera plus.",
        teaser: "Camille, consultante, a trouvé {montant} de plus par an en changeant d'option d'impôt. Et vous ?",
        aVenir: "À venir",
        verifiee: "Trouvée dans l'annuaire public",
        statut: "Statut",
        activite: "Activité",
        creee: "Créée le",
        siege: "Siège",
        numeroTva: "N° de TVA",
        codeApe: "Code APE {code}",
        ilYa: "il y a {duree}",
        ceMois: "ce mois-ci",
        ans: (n) => (n === 1 ? "1 an" : `${n} ans`),
        mois: (n) => `${n} mois`,
        et: "et",
        individuel: "Entrepreneur individuel",
        eurl: "EURL",
        sasu: "SASU",
        sarl: "SARL",
        modifier: "Modifier",
        microOuReel: "Micro ou réel ?",
        micro: "Micro-entreprise",
        reel: "Au réel",
        microDetail: "Auto-entrepreneur, c'est la micro.",
        seulAssocie: "Seul associé ?",
        seul: "Oui, je suis seul",
        plusieurs: "Nous sommes plusieurs",
        quelleActivite: "Que vendez-vous ?",
        quelleActiviteProjet: "Que ferez-vous ?",
        activites: { bnc: "Une activité libérale", services: "Des prestations", vente: "Des marchandises", cipav: "Une profession réglementée" },
        activitesExemples: {
            bnc: "Conseil, création, formation : consultant, graphiste, développeur freelance, coach",
            services: "Un métier commercial ou manuel : votre app ou SaaS sur abonnement, artisan, coiffeur, VTC",
            vente: "Vous vendez des produits : e-commerce, revente, restauration, chambres d'hôtes",
            cipav: "Une caisse de retraite à part : architecte, ostéopathe, psychologue, moniteur de ski",
        },
        nonCouverte: "Nos calculs couvrent la micro-entreprise, l'entreprise individuelle au réel, l'EURL et la SASU. Cette forme n'en fait pas partie : répondez plutôt aux questions.",
        autre: "Ce n'est pas elle",
        encaisse: "J'encaisse",
        encaisseProjet: "J'encaisserai",
        parAnHt: "par an, hors taxes.",
        glisser: "Faites glisser, tout se recalcule.",
        caLibelle: "Chiffre d'affaires par an, hors taxes",
        caPublie: "Prérempli avec le chiffre d'affaires déposé pour {annee}.",
        acre: "J'ai l'ACRE",
        acreProjet: "Je demanderai l'ACRE",
        acreJusqua: "Cotisations réduites jusqu'au {date}.",
        acreJusquaProjet: "Créée {debut}, votre micro paierait des cotisations réduites jusqu'au {date}.",
        aujourdhuiMot: "aujourd'hui",
        leJour: "le {date}",
        acreAstuce:
            "Astuce : créée le {debut}, dans {jours}, elle garderait l'ACRE jusqu'au {fin}, trois mois de plus. L'ACRE court jusqu'à la fin du troisième trimestre après la création : démarrer le premier jour d'un trimestre donne douze mois pleins.",
        acreAstuceCfe: "Et comme aucune CFE n'est due l'année de création, votre première CFE recule aussi d'un an.",
        acreGain: "{montant} de cotisations en moins cette année.",
        acreDelai: "Encore {jours} pour la demander.",
        voirTout: "Voir tout ce qui m'attend",
        confidentiel: "Vos réponses restent sur cet appareil.",
        situation: "Votre situation",
        aucunPoint: "Rien d'urgent pour l'instant.",
        autres: "Et {n} autres points à regarder.",
        autrePoint: "Et un autre point à regarder.",
        voir: "Voir ma situation",
        votreProjet: "Votre projet",
        votreDossier: "Votre dossier",
        modifier2: "Modifier",
        supprimer: "Supprimer",
        changerEntreprise: "Changer d'entreprise",
        entrepriseCreee: "Mon entreprise est créée",
        chercherEntreprise: "Retrouver mon entreprise",
        annulerChangement: "Garder mon dossier",
        effacerTexte: "Les outils ne se rempliront plus. Les informations des formulaires (nom, adresse, SIRET) restent sur cet appareil.",
        marques: { qualiopi: "Qualiopi", formation: "Organisme de formation", rge: "RGE", ess: "ESS", mission: "Société à mission", bio: "Bio" },
        dates: {
            "acre-demande": "Demander l'ACRE",
            "acre-fin": "Fin de l'ACRE",
            "vfl-option": "Option pour l'impôt",
            "vfl-option-annuelle": "Option pour l'impôt",
            "vfl-renoncer": "Option pour l'impôt",
            "cfe-declaration": "Déclarer la CFE",
            "cfe-premier-solde": "Première CFE",
            "cfe-solde": "CFE",
            liasse: "Liasse fiscale",
            solde: "Solde de l'IS",
            approbation: "Approbation des comptes",
            depot: "Dépôt des comptes",
            acompteIs: "Acompte d'IS",
            cfe: "CFE",
            cfeDeclaration: "Déclarer la CFE",
            tvaAcompteDecembre: "Acompte de TVA",
        },
    },
    en: {
        ongletEntreprise: "I have a business",
        ongletProjet: "I'm starting out",
        appel: "We'll tell you what's coming.",
        appelProjet: "Two answers, and it's worked out.",
        bonjour: "Hello {prenom}.",
        trouvee: "Found it.",
        connu: "Here's what the register already knows.",
        champ: "Company name or SIRET",
        annuaire: "French public register",
        recherche: "Searching the register…",
        aucun: "No active company has this name. Try your SIRET, or answer the questions.",
        occupe: "The public register is not answering right now. Try again in a moment, or answer the questions.",
        trouvees: "{n} companies found in the public register.",
        trouveeUne: "One company found in the public register.",
        rienGarde: "Nothing is kept until you choose.",
        depuis: "since {date}",
        questions: "Answer the questions",
        sansRecherche: "Answer the questions instead",
        vous: "You",
        direct: "Worked out live",
        parMois: "a month for her",
        parMoisVous: "a month for you",
        apres: "after Urssaf, income tax and the training levy",
        tva: "VAT threshold",
        plafond: "Micro ceiling",
        charges: "Contributions and tax",
        parAn: "a year",
        dansJours: "in {jours}",
        passe: "passed on {date}",
        sousSeuil: "No",
        sousSeuilDetail: "below {base}",
        janvier: "1 Jan",
        janvierDetail: "next year",
        due: "Due",
        dueDetail: "since 1 January",
        dePlafond: "of {plafond}",
        mystere: "Your figures show up here",
        envoyerAmi: "Send to a solopreneur friend",
        messageCopie: "Message copied, paste it into your conversation",
        accueilAmi: "A friend sent you this page.",
        accueilAmiSuite: "Type your company's name: you'll see your own dates in a few answers.",
        tvaDeja: "Already charged",
        tvaDejaDetail: "on your invoices",
        tvaFacturee: "I already charge VAT",
        tvaFactureeDetail: "By choice, or after passing the threshold in an earlier year: the tool won't announce it any more.",
        teaser: "Camille, a consultant, found {montant} more a year by switching her tax option. What about you?",
        aVenir: "Coming up",
        verifiee: "Found in the public register",
        statut: "Status",
        activite: "Activity",
        creee: "Created on",
        siege: "Registered office",
        numeroTva: "VAT number",
        codeApe: "APE code {code}",
        ilYa: "{duree} ago",
        ceMois: "this month",
        ans: (n) => (n === 1 ? "1 year" : `${n} years`),
        mois: (n) => (n === 1 ? "1 month" : `${n} months`),
        et: "and",
        individuel: "Sole trader",
        eurl: "EURL",
        sasu: "SASU",
        sarl: "SARL",
        modifier: "Change",
        microOuReel: "Micro or actual expenses?",
        micro: "Micro-enterprise",
        reel: "Actual expenses",
        microDetail: "Auto-entrepreneur means micro.",
        seulAssocie: "Sole partner?",
        seul: "Yes, just me",
        plusieurs: "There are several of us",
        quelleActivite: "What do you sell?",
        quelleActiviteProjet: "What will you do?",
        activites: { bnc: "A liberal profession", services: "Services", vente: "Goods", cipav: "A regulated profession" },
        activitesExemples: {
            bnc: "Advice, design, training: consultant, designer, freelance developer, coach",
            services: "A commercial or manual trade: your own app or SaaS on subscription, craftsman, hairdresser, ride-hailing",
            vente: "You sell products: e-commerce, resale, food service, guest rooms",
            cipav: "A separate pension fund: architect, osteopath, psychologist, ski instructor",
        },
        nonCouverte: "Our calculations cover micro-enterprises, sole traders on actual expenses, EURLs and SASUs. This legal form isn't one of them: answer the questions instead.",
        autre: "That's not it",
        encaisse: "I bring in",
        encaisseProjet: "I'll bring in",
        parAnHt: "a year, excluding VAT.",
        glisser: "Slide it, everything updates.",
        caLibelle: "Revenue per year, excluding VAT",
        caPublie: "Prefilled with the revenue filed for {annee}.",
        acre: "I have the ACRE",
        acreProjet: "I'll apply for the ACRE",
        acreJusqua: "Reduced contributions until {date}.",
        acreJusquaProjet: "Started {debut}, your micro would pay reduced contributions until {date}.",
        aujourdhuiMot: "today",
        leJour: "on {date}",
        acreAstuce:
            "Tip: started on {debut}, in {jours}, it would keep the ACRE until {fin}, three months longer. The ACRE runs to the end of the third quarter after you start: starting on the first day of a quarter gives twelve full months.",
        acreAstuceCfe: "And since no CFE is due in the year of creation, your first CFE moves back a year too.",
        acreGain: "{montant} less in contributions this year.",
        acreDelai: "{jours} left to apply.",
        voirTout: "See everything ahead of me",
        confidentiel: "Your answers stay on this device.",
        situation: "Your situation",
        aucunPoint: "Nothing urgent for now.",
        autres: "And {n} other points to look at.",
        autrePoint: "And one other point to look at.",
        voir: "See my situation",
        votreProjet: "Your project",
        votreDossier: "Your profile",
        modifier2: "Edit",
        supprimer: "Delete",
        changerEntreprise: "Change company",
        entrepriseCreee: "My company is registered",
        chercherEntreprise: "Find my company",
        annulerChangement: "Keep my profile",
        effacerTexte: "The tools will no longer fill themselves in. Your form details (name, address, SIRET) stay on this device.",
        marques: { qualiopi: "Qualiopi", formation: "Training provider", rge: "RGE", ess: "Social economy", mission: "Mission-driven company", bio: "Organic" },
        dates: {
            "acre-demande": "Apply for the ACRE",
            "acre-fin": "End of the ACRE",
            "vfl-option": "Tax option",
            "vfl-option-annuelle": "Tax option",
            "vfl-renoncer": "Tax option",
            "cfe-declaration": "CFE return",
            "cfe-premier-solde": "First CFE",
            "cfe-solde": "CFE",
            liasse: "Tax return",
            solde: "Corporation tax balance",
            approbation: "Approval of accounts",
            depot: "Filing of accounts",
            acompteIs: "Corporation tax instalment",
            cfe: "CFE",
            cfeDeclaration: "CFE return",
            tvaAcompteDecembre: "VAT instalment",
        },
    },
};

const BOUTON = "inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-[#D04515] px-5 py-3 text-base font-semibold text-white hover:text-white hover:bg-[#B83C12] transition-colors duration-200 disabled:opacity-50";
const LIEN = "font-semibold text-gray-900 dark:text-white underline underline-offset-2 decoration-gray-300 dark:decoration-white/30 hover:decoration-primary transition-colors duration-200";
const PUCE = "inline-flex min-h-[40px] items-center rounded-full border px-4 text-sm font-semibold transition-colors duration-200";
const APPEL = "mb-0 text-base sm:text-[17px] font-semibold text-gray-900 dark:text-white";
const DOUX = "font-medium text-gray-500 dark:text-lightBlue/70";
const ETIQUETTE = "text-xs text-gray-500 dark:text-lightBlue/70";

// La courbe des fondus de la maison : rapide au départ, posée à l'arrivée.
const COURBE = [0.22, 1, 0.36, 1];

/** « CAMILLE MARTIN (ENSEIGNE) » → « Camille », pour un entrepreneur individuel seulement. */
function prenomDe(entreprise) {
    if (!entreprise) return null;
    if (entreprise.prenom) return entreprise.prenom;
    if (!entreprise.individuel) return null;
    const mots = entreprise.nom.replace(/\(.*\)/, "").trim().split(/\s+/);
    if (mots.length < 2) return null;
    const p = mots[0].toLowerCase();
    return p.charAt(0).toUpperCase() + p.slice(1);
}

/** Le curseur s'arrête au-delà du plafond de l'activité, avec de la marge. */
function maximumCurseur(activite, statut) {
    if (statut === "eurl" || statut === "eurlIr" || statut === "sasu" || statut === "reel") return 300000;
    return activite === "vente" ? 250000 : 120000;
}

const PETITS_MOTS = new Set(["le", "la", "les", "de", "du", "des", "sur", "sous", "en", "et", "lès", "lez", "aux", "au"]);

/** « SOISY-SOUS-MONTMORENCY » → « Soisy-sous-Montmorency (95) » ; « LYON » 69003 → « Lyon 3e ». */
export function communeLisible(e, lang) {
    if (!e?.commune) return null;
    const nom = e.commune
        .toLowerCase()
        .split(/([\s-])/)
        .map((m, i) => (i > 0 && PETITS_MOTS.has(m) ? m : m.charAt(0).toUpperCase() + m.slice(1)))
        .join("");
    const cp = Number(e.codePostal);
    const arrondissement =
        nom === "Paris" && cp > 75000 && cp <= 75020 ? cp - 75000 : nom === "Lyon" && cp > 69000 && cp <= 69009 ? cp - 69000 : nom === "Marseille" && cp > 13000 && cp <= 13016 ? cp - 13000 : null;
    if (arrondissement) {
        if (lang === "en") return `${nom} ${arrondissement}${arrondissement === 1 ? "st" : arrondissement === 2 ? "nd" : arrondissement === 3 ? "rd" : "th"}`;
        return `${nom} ${arrondissement === 1 ? "1er" : `${arrondissement}e`}`;
    }
    return e.departement ? `${nom} (${e.departement})` : nom;
}

/** « il y a 1 an et 7 mois », depuis la date de début jusqu'à aujourd'hui. */
function ageDe(dateDebut, aujourdhui, t) {
    if (!dateDebut) return null;
    const [a1, m1, j1] = dateDebut.split("-").map(Number);
    const [a2, m2, j2] = aujourdhui.split("-").map(Number);
    let mois = (a2 - a1) * 12 + (m2 - m1) - (j2 < j1 ? 1 : 0);
    if (mois < 0) return null;
    if (mois === 0) return t.ceMois;
    const ans = Math.floor(mois / 12);
    mois %= 12;
    const duree = [ans ? t.ans(ans) : null, mois ? t.mois(mois) : null].filter(Boolean).join(` ${t.et} `);
    return remplir(t.ilYa, { duree });
}

/** « FR88950731331 » → « FR88 950 731 331 ». */
function tvaLisible(n) {
    return n ? `${n.slice(0, 4)} ${n.slice(4, 7)} ${n.slice(7, 10)} ${n.slice(10)}` : null;
}

/** La forme juridique en deux mots, pour la liste comme pour la fiche. */
export function formeDe(e, t) {
    if (!e) return null;
    if (e.individuel) return t.individuel;
    if (e.sarl) return t.sarl;
    return e.statut ? t[e.statut] : null;
}

/**
 * LA BANDE DES TROIS CHIFFRES, pour l'exemple comme pour le lecteur. Une
 * ligne et non une carte : on la lit d'un coup d'œil, et elle reste à la même
 * place quand Camille devient « Vous ». Pour le lecteur, elle finit par les
 * dates qui l'attendent.
 */
function Bande({ r, t, lang, libelle, pourElle, direct, estompee, mystere = false, dates = [], outils, children }) {
    // LE FLOU QUI DONNE ENVIE. Avant la réponse, les étiquettes se lisent
    // (ce qu'on va savoir) mais pas les chiffres (ce qu'on saura de soi).
    const flou = mystere ? "blur-[7px] select-none" : "";
    const annee = Number(r.aujourdhui.slice(0, 4));
    const tva = r.tva;
    let tvaValeur = "…";
    let tvaDetail = "";
    if (tva) {
        if (tva.statut === "assujetti_immediat" && tva.date) {
            tvaValeur = tva.passe ? t.due : dateCourte(tva.date, lang, annee);
            tvaDetail = tva.passe ? remplir(t.passe, { date: dateCourte(tva.date, lang, annee) }) : remplir(t.dansJours, { jours: nJours(tva.joursRestants, lang) });
        } else if (tva.statut === "bascule_janvier_prochain") {
            tvaValeur = t.janvier;
            tvaDetail = t.janvierDetail;
        } else if (tva.statut === "assujetti_depuis_janvier") {
            tvaValeur = t.due;
            tvaDetail = t.dueDetail;
        } else if (tva.statut === "deja") {
            tvaValeur = t.tvaDeja;
            tvaDetail = t.tvaDejaDetail;
        } else {
            tvaValeur = t.sousSeuil;
            tvaDetail = remplir(t.sousSeuilDetail, { base: euros(tva.base, lang) });
        }
    }
    const alerteTva = tva && tva.statut !== "franchise" && tva.statut !== "deja";
    const enMicro = r.dossier.statut === "micro" || r.dossier.statut === "projet";
    const alertePlafond = enMicro && r.plafond && r.plafond.part >= 0.9;

    const colonne = (titre, valeur, detail, alerte) => (
        <div className="min-w-0 sm:border-l sm:border-gray-200/80 sm:pl-5 dark:sm:border-white/10">
            <div className={`text-xs font-semibold ${alerte && !mystere ? "text-[#B83C12] dark:text-[#FF8A5B]" : "text-gray-500 dark:text-lightBlue/70"}`}>{titre}</div>
            <div className={`text-xl font-bold tabular-nums font-sourceSans text-gray-900 dark:text-white ${flou}`}>{valeur}</div>
            <div className={`text-[13px] text-gray-500 dark:text-lightBlue/70 ${flou}`}>{detail}</div>
        </div>
    );

    return (
        <div
            className={`rounded-2xl border px-4 py-4 sm:px-5 transition-[opacity,background-color,border-color] duration-300 ${
                direct ? "border-gray-300 bg-white dark:border-white/20 dark:bg-white/[0.05]" : "border-gray-200/80 bg-white/60 dark:border-white/10 dark:bg-white/[0.02]"
            } ${estompee ? "opacity-50" : "opacity-100"}`}
            aria-live="polite"
        >
            <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-lightBlue/70">{libelle}</span>
                {direct && <span className="rounded-full bg-green-50 dark:bg-green-500/10 px-2 py-0.5 text-xs font-semibold text-green-700 dark:text-green-400">{t.direct}</span>}
            </div>
            <div className="relative">
            <div className="mt-2.5 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-[1.3fr_1fr_1fr] sm:gap-x-5" aria-hidden={mystere || undefined}>
                <div className="col-span-2 min-w-0 sm:col-span-1">
                    <div className="flex flex-wrap items-baseline gap-x-1.5">
                        <span className={`text-3xl font-bold tabular-nums font-sourceSans text-gray-900 dark:text-white ${flou}`}>{r.revenu ? euros(Math.round(r.revenu.mensuel), lang) : "…"}</span>
                        <span className="text-sm text-gray-600 dark:text-lightBlue">{pourElle ? t.parMois : t.parMoisVous}</span>
                    </div>
                    <div className="text-[13px] text-gray-500 dark:text-lightBlue/70">{t.apres}</div>
                </div>
                {colonne(t.tva, tvaValeur, tvaDetail, alerteTva)}
                {enMicro && r.plafond
                    ? colonne(t.plafond, pourcent(r.plafond.part, lang), remplir(t.dePlafond, { plafond: euros(r.plafond.plafond, lang) }), alertePlafond)
                    : colonne(t.charges, prelevementsDe(r.revenu) !== null ? euros(Math.round(prelevementsDe(r.revenu)), lang) : "…", t.parAn, false)}
            </div>
            {mystere && (
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-900/90 dark:bg-white/90 px-3.5 py-1.5 text-xs font-semibold text-white dark:text-gray-900 shadow-lg">
                        <SolarIcon name="lock-keyhole-minimalistic-linear" className="h-3.5 w-3.5" />
                        {t.mystere}
                    </span>
                </span>
            )}
            </div>
            {dates.length > 0 && (
                <div className="mt-3.5 flex flex-wrap items-center gap-2 border-t border-gray-100 dark:border-white/10 pt-3">
                    <span className="mr-1 text-xs font-semibold text-gray-500 dark:text-lightBlue/70">{t.aVenir}</span>
                    {dates.map((e) => {
                        const contenu = (
                            <>
                                <SolarIcon name="calendar-minimalistic-linear" className="h-3.5 w-3.5 text-gray-500 dark:text-lightBlue/70" />
                                <span className="font-semibold text-gray-900 dark:text-white group-hover:text-[#B83C12] dark:group-hover:text-[#FF8A5B] transition-colors duration-200">{t.dates[e.cle]}</span>
                                <span className="text-gray-500 dark:text-lightBlue/70">{dateCourte(e.date, lang, annee)}</span>
                            </>
                        );
                        const pastille = "group inline-flex min-h-[32px] items-center gap-1.5 rounded-full bg-gray-100/80 dark:bg-white/[0.06] px-2.5 py-1 text-[13px]";
                        // CHAQUE DATE MÈNE À L'OUTIL QUI LA TRAITE, comme le
                        // faisait la frise : « Demander l'ACRE » ouvre le formulaire.
                        const slug = outils?.[e.outil]?.slug;
                        return slug ? (
                            <Link key={e.fait || e.cle} href={{ pathname: "/tools/[slug]", params: { slug } }} className={`${pastille} transition-colors duration-200 hover:bg-[#fff4ee] dark:hover:bg-[#D04515]/15`}>
                                {contenu}
                                <SolarIcon name="arrow-right-linear" className="h-3.5 w-3.5 text-gray-400 group-hover:text-[#B83C12] dark:group-hover:text-[#FF8A5B]" />
                            </Link>
                        ) : (
                            <span key={e.fait || e.cle} className={pastille}>
                                {contenu}
                            </span>
                        );
                    })}
                </div>
            )}
            {children}
        </div>
    );
}

/** Ce que l'Urssaf et les impôts prennent sur l'année, quel que soit le statut. */
function prelevementsDe(revenu) {
    if (!revenu) return null;
    if (Number.isFinite(revenu.prelevements)) return revenu.prelevements;
    return revenu.cotisations !== null && revenu.cotisations !== undefined ? revenu.cotisations + (revenu.impot || 0) : null;
}

/** Les deux portes, côte à côte : l'une ne se cache pas sous l'autre. */
function Onglets({ t, mode, onChanger, reduit }) {
    return (
        <div className="inline-flex shrink-0 self-start rounded-full border border-gray-200/80 dark:border-white/10 bg-white/70 dark:bg-white/[0.04] p-1">
            {[
                ["entreprise", t.ongletEntreprise, "case-minimalistic-linear"],
                ["projet", t.ongletProjet, "rocket-2-linear"],
            ].map(([cle, libelle, icone]) => {
                const actif = mode === cle;
                return (
                    <button
                        key={cle}
                        type="button"
                        aria-pressed={actif}
                        onClick={() => onChanger(cle)}
                        className={`relative inline-flex min-h-[36px] items-center gap-1.5 rounded-full px-3.5 text-sm font-semibold transition-colors duration-200 ${
                            actif ? "text-white dark:text-gray-900" : "text-gray-600 hover:text-gray-900 dark:text-lightBlue dark:hover:text-white"
                        }`}
                    >
                        {actif && <motion.span layoutId={reduit ? undefined : "situation-onglet"} transition={{ duration: 0.25, ease: COURBE }} className="absolute inset-0 rounded-full bg-gray-900 dark:bg-white" />}
                        <SolarIcon name={icone} className={`relative h-4 w-4 ${!actif && cle === "projet" ? "text-[#D04515] dark:text-[#FF8A5B]" : ""}`} />
                        <span className="relative">{libelle}</span>
                    </button>
                );
            })}
        </div>
    );
}

export default function ToolsSituationStart({ lang = "fr", aujourdhui, outils }) {
    const t = T[lang] || T.fr;
    const hydrate = useHydrate();
    const router = useRouter();
    const reduit = useReducedMotion();
    const { profil, enregistrer, effacer: effacerDossier } = useSolopreneurProfile();
    const { identite, enregistrer: enregistrerIdentite } = useIdentite();
    const { entreprise: entrepriseGardee, enregistrer: garderEntreprise, effacer: oublierEntreprise } = useEntrepriseChoisie();

    const [mode, setMode] = useState("entreprise");
    // ARRIVÉ PAR UN AMI : le lien partagé porte utm_source=carte (ou ami).
    // L'adresse se lit comme une source externe : vide côté serveur, lue dans
    // le navigateur après l'hydratation.
    const depuisUnAmi = useSyncExternalStore(
        () => () => {},
        () => SOURCES_AMI.includes(new URLSearchParams(window.location.search).get("utm_source")),
        () => false,
    );
    const [messageCopie, setMessageCopie] = useState(false);
    const [etape, setEtape] = useState("recherche");
    const [entreprise, setEntreprise] = useState(null);
    const [choix, setChoix] = useState({ statut: null, activite: null });
    const [ca, setCa] = useState(40000);
    // L'année du chiffre d'affaires déposé qui a prérempli le curseur.
    const [caDepose, setCaDepose] = useState(null);
    const [acre, setAcre] = useState(false);
    // La TVA déjà facturée : l'outil ne l'annonce plus (dossier `tvaFacturee`).
    const [tvaFacturee, setTvaFacturee] = useState(false);
    // « Modifier » rouvre la fiche : le résumé du dossier ne revient plus
    // tant que le lecteur n'a pas fini, même s'il repart d'une recherche.
    const [enModification, setEnModification] = useState(false);
    const [confirmer, setConfirmer] = useState(false);

    const rempli = hydrate && !enModification && !dossierVide(profil) && profil.caCetteAnnee !== null && etape === "recherche" && mode === "entreprise";
    const exemple = useMemo(() => situationDuDossier(EXEMPLE, aujourdhui), [aujourdhui]);
    const annee = Number(aujourdhui.slice(0, 4));

    const projet = choix.statut === "projet";

    // Le dossier tel qu'il serait avec les réponses en cours : c'est lui que
    // les trois chiffres calculent pendant qu'on fait glisser le curseur.
    const enCours = useMemo(() => {
        const statut = choix.statut || profil.statut;
        const activite = choix.activite || profil.activite;
        if (!statut || !activite || statut === "plusieurs") return null;
        // Un projet se calcule comme une micro qui commence aujourd'hui.
        const base = { ...profil, statut: statut === "projet" ? "micro" : statut, activite, caCetteAnnee: ca, acre, tvaFacturee };
        if (statut === "projet") base.dateDebut = aujourdhui;
        return situationDuDossier(base, aujourdhui);
    }, [choix, profil, ca, acre, tvaFacturee, aujourdhui]);

    const situation = useMemo(() => (rempli ? situationDuDossier(profil, aujourdhui) : null), [rempli, profil, aujourdhui]);

    const couverte = projet || (entreprise?.prisEnCharge && choix.statut !== "plusieurs");
    const pret = couverte && choix.statut && choix.activite;

    // L'ACRE ne se propose qu'en micro, et tant qu'elle touche l'année.
    const acreProposee = Boolean(enCours?.acre?.cetteAnnee && (choix.statut === "micro" || projet));
    // « Je facture déjà la TVA » : proposé à qui a une entreprise, quand
    // l'outil s'apprête à annoncer la TVA (ou que la réponse est déjà oui).
    const tvaProposee = Boolean(!projet && enCours?.tva && (tvaFacturee || enCours.tva.statut !== "franchise"));

    const choisir = (e) => {
        setEntreprise(e);
        setChoix({ statut: e.statut, activite: e.activite || null });
        setEtape("trouvee");
    };

    const recommencer = () => {
        setEntreprise(null);
        setChoix({ statut: null, activite: null });
        setEtape("recherche");
    };

    const changerMode = (m) => {
        if (m === mode) return;
        setMode(m);
        if (m === "projet") {
            setEntreprise(null);
            setChoix({ statut: "projet", activite: null });
            setEtape("trouvee");
        } else {
            recommencer();
        }
    };

    // PAS DE BOUTON « CONTINUER ». Dès que la fiche est complète, le curseur
    // arrive dans la même fiche. Le délai laisse finir la cascade des
    // informations de l'annuaire, ou voir la puce qu'on vient de toucher.
    useEffect(() => {
        if (etape !== "trouvee" || !pret) return undefined;
        const minuteur = setTimeout(() => {
            const champs = { ...dossierDepuisEntreprise(entreprise), statut: choix.statut, activite: choix.activite };
            // Un projet n'a pas de date : on efface celle d'une entreprise
            // choisie avant de changer d'onglet.
            if (projet) champs.dateDebut = profil.statut === "projet" ? profil.dateDebut : null;
            // Et il n'a pas d'entreprise : son nom et son SIREN s'en vont
            // avec elle.
            if (projet) {
                champs.entrepriseNom = null;
                champs.siren = null;
            }
            enregistrer(champs);
            // Le SIRET et le code APE rempliront les formulaires ; ils ne
            // quittent pas l'appareil (l'identité n'est jamais envoyée).
            if (entreprise?.siret && !identite.siret) enregistrerIdentite({ siret: entreprise.siret, ...(entreprise.ape ? { codeApe: entreprise.ape } : {}) });
            // La fiche aussi reste sur l'appareil, pour la reconnaître au retour.
            garderEntreprise(projet ? null : entreprise);
            const max = maximumCurseur(choix.activite, choix.statut);
            // Une société qui dépose ses comptes a publié son chiffre
            // d'affaires : s'il est récent, il remplace notre valeur de départ.
            const depose = entreprise?.caPublie && entreprise.caPublie.annee >= annee - 2 ? entreprise.caPublie : null;
            setCaDepose(depose ? depose.annee : null);
            setCa(Math.min(max, depose ? depose.ca : (profil.caCetteAnnee ?? (choix.activite === "vente" ? 80000 : 40000))));
            setAcre(profil.acre === true);
            setTvaFacturee(profil.tvaFacturee === true);
            setEtape("curseur");
        }, reduit ? 0 : 650);
        return () => clearTimeout(minuteur);
        // Seules l'étape et les réponses déclenchent la suite.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [etape, pret, choix.statut, choix.activite]);

    const terminer = () => {
        enregistrer({ statut: choix.statut, activite: choix.activite, caCetteAnnee: ca, ...(acreProposee ? { acre } : {}), ...(tvaProposee ? { tvaFacturee } : {}) });
        router.push("/tools/my-situation");
    };

    // MODIFIER ROUVRE LA FICHE AU CURSEUR, avec ce que le dossier sait déjà :
    // un chiffre à corriger ou l'ACRE à cocher, sans refaire la recherche.
    const modifier = () => {
        const statut = profil.statut;
        const estProjet = statut === "projet";
        // Une fiche gardée ne vaut que si elle dit le même statut que le dossier.
        const fiche = !estProjet && entrepriseGardee ? entrepriseGardee : null;
        setEnModification(true);
        setMode(estProjet ? "projet" : "entreprise");
        setEntreprise(fiche);
        setChoix({ statut, activite: profil.activite });
        setCa(Math.min(maximumCurseur(profil.activite, statut), profil.caCetteAnnee ?? 40000));
        setCaDepose(null);
        setAcre(profil.acre === true);
        setTvaFacturee(profil.tvaFacturee === true);
        setEtape(statut && profil.activite ? "curseur" : "trouvee");
    };

    // CHANGER D'ENTREPRISE, c'est repartir de la barre sans rien effacer :
    // le dossier reste tant qu'une autre fiche n'est pas validée.
    const changerEntreprise = () => {
        setEnModification(true);
        setMode("entreprise");
        recommencer();
    };

    const supprimer = () => {
        effacerDossier();
        oublierEntreprise();
        setEnModification(false);
        recommencer();
    };

    const fondu = (delai = 0) => (reduit ? {} : { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.28, ease: COURBE, delay: delai } });

    // ------------------------------------------------------------------
    // Déjà un dossier : ses chiffres, et le premier point à regarder
    // ------------------------------------------------------------------
    if (rempli && situation) {
        const tSit = T_SITUATION[lang] || T_SITUATION.fr;
        const presse = alertesAffichees(situation)
            .filter((a) => a.gravite !== "info")
            .map((a) => ({ ...a, contenu: texteAlerte(a, situation, tSit, lang), kicker: kickerAlerte(a, situation, tSit, lang) }))
            .filter((a) => a.contenu);
        const premier = presse[0];
        const estProjet = profil.statut === "projet";
        const fiche = !estProjet ? entrepriseGardee : null;
        const details = [
            estProjet ? null : valeurLisible("statut", profil.statut, lang),
            fiche?.apeLibelle || valeurLisible("activite", profil.activite, lang),
        ].filter(Boolean);
        const petitBouton = "inline-flex min-h-[40px] items-center gap-1.5 rounded-full border border-gray-200/80 dark:border-white/10 bg-white/70 dark:bg-white/[0.04] px-3.5 text-sm font-semibold transition-colors duration-200";
        return (
            <section className="mt-10">
                {/* LE DOSSIER SE RECONNAÎT D'UN COUP D'ŒIL, et se corrige ou
                    s'efface sans passer par la page « Votre situation ». */}
                <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-gray-200/80 dark:border-white/10 bg-white/60 dark:bg-white/[0.02] px-4 py-3 sm:flex-row sm:items-center">
                    <div className="flex min-w-0 flex-1 items-center gap-2.5">
                        <SolarIcon
                            name={estProjet ? "rocket-2-linear" : fiche ? "verified-check-bold" : "case-minimalistic-linear"}
                            className={`h-5 w-5 shrink-0 ${estProjet ? "text-[#D04515] dark:text-[#FF8A5B]" : fiche ? "text-green-600 dark:text-green-400" : "text-gray-500 dark:text-lightBlue/70"}`}
                        />
                        <div className="min-w-0">
                            <div className="truncate font-bold text-gray-900 dark:text-white">{fiche ? fiche.nom : estProjet ? t.votreProjet : t.votreDossier}</div>
                            {details.length > 0 && <div className="text-[13px] text-gray-500 dark:text-lightBlue/70">{details.join(" · ")}</div>}
                        </div>
                    </div>
                    <div className="flex shrink-0 flex-wrap gap-2">
                        <button type="button" onClick={changerEntreprise} className={`${petitBouton} text-gray-800 hover:border-gray-300 dark:text-white dark:hover:border-white/25`}>
                            <SolarIcon name={estProjet ? "rocket-2-linear" : "magnifer-linear"} className="h-4 w-4" />
                            {fiche ? t.changerEntreprise : estProjet ? t.entrepriseCreee : t.chercherEntreprise}
                        </button>
                        <button type="button" onClick={modifier} className={`${petitBouton} text-gray-800 hover:border-gray-300 dark:text-white dark:hover:border-white/25`}>
                            <SolarIcon name="pen-linear" className="h-4 w-4" />
                            {t.modifier2}
                        </button>
                        <button type="button" onClick={() => setConfirmer(true)} className={`${petitBouton} text-gray-600 hover:border-red-200 hover:text-red-600 dark:text-lightBlue dark:hover:text-red-400`}>
                            <SolarIcon name="trash-bin-minimalistic-linear" className="h-4 w-4" />
                            {t.supprimer}
                        </button>
                    </div>
                </div>

                <BottomSheetModal isOpen={confirmer} onClose={() => setConfirmer(false)} labelledBy="situation-effacer-titre">
                    <div className="p-6 sm:p-8">
                        <h2 id="situation-effacer-titre" className="mb-2 text-xl font-bold font-sourceSans dark:text-white">
                            {tSit.effacerTitre}
                        </h2>
                        <p className="mb-6 text-sm sm:text-base text-gray-600 dark:text-lightBlue">{t.effacerTexte}</p>
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <button type="button" onClick={() => setConfirmer(false)} className="inline-flex min-h-[44px] items-center justify-center rounded-xl border border-gray-300 dark:border-white/20 px-5 py-3 text-sm font-semibold text-gray-800 dark:text-white hover:bg-gray-50 dark:hover:bg-white/5">
                                {tSit.annuler}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    supprimer();
                                    setConfirmer(false);
                                }}
                                className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-red-600 px-5 py-3 text-sm font-semibold text-white hover:bg-red-700"
                            >
                                {tSit.effacerOui}
                            </button>
                        </div>
                    </div>
                </BottomSheetModal>

                {premier ? (
                    <>
                        <div className="text-xs font-semibold uppercase tracking-wider text-[#B83C12] dark:text-[#FF8A5B]">{premier.kicker}</div>
                        <p className={`${APPEL} mt-1 mb-3`}>{premier.contenu.titre}</p>
                    </>
                ) : (
                    <p className={`${APPEL} mb-3`}>
                        {t.situation}. <span className={DOUX}>{t.aucunPoint}</span>
                    </p>
                )}
                <Bande r={situation} t={t} lang={lang} libelle={t.situation} direct={false} dates={datesDe(situation, t)} outils={outils} />
                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                    <Link href="/tools/my-situation" className={BOUTON}>
                        {t.voir}
                        <SolarIcon name="arrow-right-linear" className="h-5 w-5" />
                    </Link>
                    {presse.length > 1 && (
                        <p className="mb-0 text-sm text-gray-600 dark:text-lightBlue">{presse.length === 2 ? t.autrePoint : remplir(t.autres, { n: presse.length - 1 })}</p>
                    )}
                </div>
            </section>
        );
    }

    const direct = etape === "curseur" && enCours;
    const prenom = prenomDe(entreprise);

    // La phrase d'appel suit l'étape : elle promet, puis elle salue.
    let appel = [null, t.appel];
    if (projet) appel = [null, t.appelProjet];
    else if (etape !== "recherche") appel = [prenom ? remplir(t.bonjour, { prenom }) : t.trouvee, t.connu];

    return (
        <section className="mt-10">
            <div className="mb-3 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                <AnimatePresence mode="wait" initial={false}>
                    <motion.p key={appel.join()} className={APPEL} {...(reduit ? {} : { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.18 } })}>
                        {appel[0] && <>{appel[0]} </>}
                        <span className={appel[0] ? DOUX : ""}>{appel[1]}</span>
                    </motion.p>
                </AnimatePresence>
                <Onglets t={t} mode={mode} onChanger={changerMode} reduit={reduit} />
            </div>

            {etape === "recherche" ? (
                <>
                    {/* L'ACCUEIL DE CELUI QUI ARRIVE PAR UN AMI. Il ne sait pas ce
                        qu'on attend de lui : une phrase le lui dit, au-dessus de
                        la barre qu'il va remplir. */}
                    {depuisUnAmi && mode === "entreprise" && (
                        <motion.div
                            className="mb-3 flex items-start gap-3 rounded-2xl border border-primary/25 bg-[#fff8f4] dark:bg-[#D04515]/10 px-4 py-3"
                            {...fondu(0)}
                        >
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                                <SolarIcon name="letter-opened-linear" className="h-5 w-5" />
                            </span>
                            <p className="mb-0 text-sm text-gray-700 dark:text-lightBlue">
                                <span className="font-semibold text-gray-900 dark:text-white">{t.accueilAmi}</span> {t.accueilAmiSuite}
                            </p>
                        </motion.div>
                    )}
                    <Recherche t={t} lang={lang} onChoisir={choisir} reduit={reduit} focus={enModification} />
                    <p className="mt-2.5 mb-0 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                        <Link href="/tools/my-situation" className={`${LIEN} font-medium text-gray-600 dark:text-lightBlue`}>
                            {t.sansRecherche}
                        </Link>
                        {enModification && !dossierVide(profil) && (
                            <button type="button" onClick={() => setEnModification(false)} className={`${LIEN} font-medium text-gray-600 dark:text-lightBlue`}>
                                {t.annulerChangement}
                            </button>
                        )}
                    </p>
                </>
            ) : (
                <Fiche
                    t={t}
                    lang={lang}
                    aujourdhui={aujourdhui}
                    dateDebut={profil.dateDebut}
                    etape={etape}
                    entreprise={entreprise}
                    choix={choix}
                    setChoix={setChoix}
                    couverte={couverte}
                    projet={projet}
                    ca={ca}
                    setCa={setCa}
                    caDepose={caDepose}
                    acre={acre}
                    setAcre={setAcre}
                    infoAcre={acreProposee ? enCours.acre : null}
                    debutAcre={acreProposee ? enCours.dossier?.dateDebut || null : null}
                    tvaProposee={tvaProposee}
                    tvaFacturee={tvaFacturee}
                    setTvaFacturee={setTvaFacturee}
                    onRevoir={() => setEtape("trouvee")}
                    onAutre={recommencer}
                    libelleAutre={enModification ? t.changerEntreprise : t.autre}
                    reduit={reduit}
                    fondu={fondu}
                />
            )}

            {/* L'EXEMPLE DEVIENT LE LECTEUR. Tant qu'il cherche, Camille ;
                pendant qu'il répond, Camille s'efface ; dès que le curseur
                arrive, ses propres chiffres, à la même place. */}
            <div className="mt-5">
                {direct ? (
                    // LE FLOU SE LÈVE : la bande passe de floue à nette au
                    // moment où arrivent les vrais chiffres. La seule
                    // animation marquante de l'écran.
                    <motion.div {...(reduit ? {} : { initial: { opacity: 0.6, filter: "blur(6px)" }, animate: { opacity: 1, filter: "blur(0px)" }, transition: { duration: 0.5, ease: COURBE } })}>
                        <Bande r={enCours} t={t} lang={lang} libelle={t.vous} direct dates={datesDe(enCours, t)} outils={outils} />
                    </motion.div>
                ) : (
                    <Bande r={exemple} t={t} lang={lang} libelle={t.vous} mystere>
                        {/* La preuve reste en clair : ce qu'une autre y a trouvé. */}
                        {exemple.vfl && exemple.vfl.gain >= 150 && (
                            <p className="mt-3 mb-0 flex items-start gap-2 border-t border-gray-200/60 dark:border-white/10 pt-3 text-sm text-gray-700 dark:text-lightBlue">
                                <SolarIcon name="lightbulb-bolt-linear" className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                                <span>{remplir(t.teaser, { montant: euros(Math.round(exemple.vfl.gain), lang) })}</span>
                            </p>
                        )}
                    </Bande>
                )}
            </div>

            {direct && (
                <motion.div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center" {...fondu(0.1)}>
                    <button type="button" onClick={terminer} className={BOUTON}>
                        {t.voirTout}
                        <SolarIcon name="arrow-right-linear" className="h-5 w-5" />
                    </button>
                    <span className="flex items-center gap-1.5 text-[13px] text-gray-500 dark:text-lightBlue/70">
                        <SolarIcon name="lock-keyhole-minimalistic-linear" className="h-3.5 w-3.5" />
                        {t.confidentiel}
                    </span>
                </motion.div>
            )}
            {/* LE BOUCHE-À-OREILLE, AU MOMENT FORT : les chiffres viennent de se
                dévoiler. Un lien discret, pas un second bouton qui ferait
                concurrence au premier ; il envoie la carte et un message
                tiré de la situation (lib/tools/partage.js), sans montant. */}
            {direct && (
                <motion.div className="mt-3" {...fondu(0.25)}>
                    <button
                        type="button"
                        onClick={async () => {
                            if ((await partagerSituation({ r: enCours, lang, source: "ami", projet })) === "copie") {
                                setMessageCopie(true);
                                setTimeout(() => setMessageCopie(false), 3000);
                            }
                        }}
                        className={`inline-flex min-h-[40px] items-center gap-1.5 text-sm font-semibold transition-colors duration-200 ${
                            messageCopie ? "text-green-700 dark:text-green-400" : "text-gray-700 dark:text-lightBlue hover:text-[#B83C12] dark:hover:text-[#FF8A5B]"
                        }`}
                    >
                        <SolarIcon name={messageCopie ? "check-circle-bold" : "users-group-two-rounded-linear"} className="h-4 w-4" />
                        <span className={messageCopie ? "" : "underline decoration-dotted decoration-gray-400 underline-offset-4"}>{messageCopie ? t.messageCopie : t.envoyerAmi}</span>
                    </button>
                </motion.div>
            )}
        </section>
    );
}

/**
 * Les trois prochaines dates qui ont un nom court. La fin de l'ACRE ne se
 * montre qu'à celui qui l'a : pour les autres, c'est la date de demande.
 */
function datesDe(r, t) {
    if (!r?.echeances) return [];
    return r.echeances.filter((e) => t.dates[e.cle] && (e.cle !== "acre-fin" || r.acre?.obtenue)).slice(0, 3);
}

/** Le champ et sa liste : un combobox, au clavier comme au doigt. */
export function Recherche({ t, lang, onChoisir, reduit, focus }) {
    const id = useId();
    const champ = useRef(null);
    // Arrivé par « Changer d'entreprise » : on tape tout de suite.
    useEffect(() => {
        if (focus) champ.current?.focus();
    }, [focus]);
    const [q, setQ] = useState("");
    const [etat, setEtat] = useState({ statut: "vide", resultats: [] });
    const [actif, setActif] = useState(-1);
    const controleur = useRef(null);

    useEffect(() => {
        const texte = q.trim();
        if (texte.length < 3) return undefined;
        // On attend que la frappe se calme : l'annuaire limite à sept
        // requêtes par seconde, pour tout le monde.
        const minuteur = setTimeout(async () => {
            controleur.current?.abort();
            const c = new AbortController();
            controleur.current = c;
            setEtat((e) => ({ ...e, statut: "chargement" }));
            try {
                const reponse = await fetch(`/api/tools/company-search?q=${encodeURIComponent(texte)}`, { signal: c.signal });
                const donnees = await reponse.json();
                if (!reponse.ok) {
                    setEtat({ statut: "occupe", resultats: [] });
                    return;
                }
                setEtat({ statut: donnees.resultats.length ? "resultats" : "aucun", resultats: donnees.resultats });
                setActif(-1);
            } catch (e) {
                if (e.name !== "AbortError") setEtat({ statut: "occupe", resultats: [] });
            }
        }, 300);
        return () => clearTimeout(minuteur);
    }, [q]);

    const liste = etat.resultats;
    const ouverte = etat.statut !== "vide";
    const clavier = (e) => {
        if (!liste.length) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActif((a) => (a + 1) % liste.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActif((a) => (a <= 0 ? liste.length - 1 : a - 1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            onChoisir(liste[actif >= 0 ? actif : 0]);
        } else if (e.key === "Escape") {
            setEtat({ statut: "vide", resultats: [] });
        }
    };
    const annee = new Date().getFullYear();

    return (
        <div className="relative">
            {/* La barre et la fiche partagent leur `layoutId` : au choix d'une
                entreprise, c'est la barre elle-même qui s'ouvre en fiche. */}
            <motion.label
                layoutId={reduit ? undefined : "situation-fiche"}
                transition={{ duration: 0.3, ease: COURBE }}
                style={{ borderRadius: 16 }}
                className="flex h-14 items-center gap-3 border border-gray-300 dark:border-white/20 bg-white dark:bg-white/[0.05] px-4 shadow-[0_10px_30px_rgba(17,24,39,0.05)] transition-[border-color,box-shadow] duration-200 focus-within:border-gray-900 dark:focus-within:border-white/60 focus-within:shadow-[0_0_0_4px_rgba(208,69,21,0.12),0_10px_30px_rgba(17,24,39,0.06)]"
            >
                <SolarIcon name="magnifer-linear" className="h-5 w-5 shrink-0 text-gray-500 dark:text-lightBlue/70" />
                <input
                    type="text"
                    role="combobox"
                    aria-expanded={ouverte && liste.length > 0}
                    aria-controls={`${id}-liste`}
                    aria-activedescendant={actif >= 0 ? `${id}-${actif}` : undefined}
                    ref={champ}
                    aria-autocomplete="list"
                    aria-label={t.champ}
                    placeholder={t.champ}
                    autoComplete="organization"
                    value={q}
                    onChange={(e) => {
                        setQ(e.target.value);
                        // Trop court pour chercher : la liste se ferme tout de suite.
                        if (e.target.value.trim().length < 3) setEtat({ statut: "vide", resultats: [] });
                    }}
                    onKeyDown={clavier}
                    className="h-full min-w-0 flex-1 bg-transparent text-base sm:text-[17px] text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-hidden"
                />
                {etat.statut === "chargement" ? (
                    <SolarIcon name="refresh-linear" className="h-5 w-5 shrink-0 animate-spin text-gray-400" />
                ) : (
                    <span className="hidden shrink-0 text-xs text-gray-500 dark:text-lightBlue/70 sm:inline">{t.annuaire}</span>
                )}
            </motion.label>

            <AnimatePresence>
                {ouverte && (
                    <motion.div
                        className="absolute left-0 right-0 z-20 mt-2 rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-bgDark-800 p-1.5 shadow-[0_20px_50px_rgba(17,24,39,0.12)]"
                        {...(reduit ? {} : { initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: 4 }, transition: { duration: 0.2, ease: COURBE } })}
                    >
                        {etat.statut === "chargement" && !liste.length && <p className="m-0 px-3 py-3 text-sm text-gray-500">{t.recherche}</p>}
                        {etat.statut === "aucun" && <p className="m-0 px-3 py-3 text-sm text-gray-600 dark:text-lightBlue">{t.aucun}</p>}
                        {etat.statut === "occupe" && <p className="m-0 px-3 py-3 text-sm text-gray-600 dark:text-lightBlue">{t.occupe}</p>}
                        {liste.length > 0 && (
                            <>
                                <p className="m-0 px-2.5 pt-1.5 pb-2 text-xs text-gray-500 dark:text-lightBlue/70">
                                    {liste.length === 1 ? t.trouveeUne : remplir(t.trouvees, { n: liste.length })} {t.rienGarde}
                                </p>
                                <ul id={`${id}-liste`} role="listbox" className="m-0 max-h-80 list-none overflow-auto p-0">
                                    {liste.map((e, i) => (
                                        <li
                                            key={e.siret || `${e.nom}-${i}`}
                                            id={`${id}-${i}`}
                                            role="option"
                                            aria-selected={i === actif}
                                            onMouseDown={(ev) => ev.preventDefault()}
                                            onClick={() => onChoisir(e)}
                                            className={`cursor-pointer rounded-xl px-2.5 py-2 transition-colors duration-150 ${i === actif ? "bg-[#fff4ee] dark:bg-[#D04515]/15" : "hover:bg-[#fff8f4] dark:hover:bg-white/5"}`}
                                        >
                                            <span className="block text-[15px] font-semibold text-gray-900 dark:text-white">{e.nom}</span>
                                            <span className="block text-[13px] text-gray-600 dark:text-lightBlue">
                                                {[
                                                    formeDe(e, t),
                                                    e.apeLibelle || (e.activite ? t.activites[e.activite] : null),
                                                    communeLisible(e, lang),
                                                    e.dateDebut ? remplir(t.depuis, { date: e.dateDebut.slice(0, 4) === String(annee) ? e.dateDebut.slice(5, 7) + "/" + e.dateDebut.slice(0, 4) : e.dateDebut.slice(0, 4) }) : null,
                                                ]
                                                    .filter(Boolean)
                                                    .join(" · ")}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

/** Un interrupteur de la fiche (ACRE, TVA déjà facturée) : un geste, une phrase qui dit ce qu'il change. */
function Interrupteur({ actif, onChange, titre, detail }) {
    return (
        <button type="button" role="switch" aria-checked={actif} onClick={onChange} className="flex min-h-[44px] items-center gap-3 text-left">
            <span className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors duration-200 ${actif ? "bg-[#D04515]" : "bg-gray-300 dark:bg-white/20"}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${actif ? "translate-x-[22px]" : "translate-x-0.5"}`} />
            </span>
            <span>
                <span className="block text-sm font-semibold text-gray-900 dark:text-white">{titre}</span>
                <span className="block text-[13px] text-gray-500 dark:text-lightBlue/70">{detail}</span>
            </span>
        </button>
    );
}

/**
 * La fiche : ce que l'annuaire a dit, la question qu'il laisse, puis le
 * chiffre d'affaires et l'ACRE. Une réponse donnée par le lecteur reste
 * modifiable d'un clic sur sa valeur.
 */
function Fiche({ t, lang, aujourdhui, dateDebut, libelleAutre, tvaProposee, tvaFacturee, setTvaFacturee, etape, entreprise, choix, setChoix, couverte, projet, ca, setCa, caDepose, acre, setAcre, infoAcre, debutAcre, onRevoir, onAutre, reduit, fondu }) {
    const statutAChoisir = entreprise?.individuel || entreprise?.sarl;
    const activiteAChoisir = projet || !entreprise?.activite;
    // « 1er octobre » : Intl écrit « 1 octobre ».
    const jourDit = (iso) => {
        const d = dateCourte(iso, lang, Number(aujourdhui.slice(0, 4)));
        return lang === "fr" ? d.replace(/^1 /, "1er ") : d;
    };
    // L'ACRE d'une micro s'arrête au dernier jour du troisième trimestre civil
    // qui suit la création (finAcre) : un projet qui attend le 1er jour du
    // trimestre suivant la garde trois mois de plus. On ne le propose que si
    // l'attente est courte, sinon le conseil coûterait plus qu'il ne rapporte.
    const astuceAcre = (() => {
        if (!projet || !infoAcre || !debutAcre) return null;
        const [a, m, j] = debutAcre.split("-").map(Number);
        if (j === 1 && (m - 1) % 3 === 0) return null;
        const mois = Math.floor((m - 1) / 3) * 3 + 3;
        const debut = new Date(Date.UTC(a + Math.floor(mois / 12), mois % 12, 1));
        const attente = Math.round((debut.getTime() - Date.UTC(a, m - 1, j)) / 86400000);
        if (attente > 45) return null;
        return { debut: debut.toISOString().slice(0, 10), fin: finAcre(debut).toISOString().slice(0, 10), attente };
    })();
    const max = maximumCurseur(choix.activite, choix.statut);
    const format = new Intl.NumberFormat(lang === "en" ? "en-GB" : "fr-FR");
    const annee = Number(aujourdhui.slice(0, 4));

    const libelleStatut = () => {
        if (!entreprise) return null;
        if (statutAChoisir && (choix.statut === "micro" || choix.statut === "reel" || choix.statut === "eurl")) return valeurLisible("statut", choix.statut, lang);
        return formeDe(entreprise, t);
    };

    // [libellé, valeur, précision, réponse du lecteur à rouvrir ?]
    const infos = entreprise
        ? [
              [t.statut, libelleStatut(), null, statutAChoisir && choix.statut ? "statut" : null],
              [
                  t.activite,
                  entreprise.apeLibelle || (choix.activite ? valeurLisible("activite", choix.activite, lang) : null),
                  entreprise.ape ? remplir(t.codeApe, { code: entreprise.ape }) : null,
                  activiteAChoisir && choix.activite ? "activite" : null,
              ],
              [t.creee, entreprise.dateDebut ? valeurLisible("dateDebut", entreprise.dateDebut, lang) : null, ageDe(entreprise.dateDebut, aujourdhui, t), null],
              [t.siege, communeLisible(entreprise, lang), null, null],
              [t.numeroTva, tvaLisible(entreprise.numeroTva), null, null],
          ].filter(([, v]) => v)
        : !projet && choix.statut
          ? // Un dossier rempli par les questions, sans fiche de l'annuaire.
            [
                [t.statut, valeurLisible("statut", choix.statut, lang), null, null],
                [t.activite, choix.activite ? valeurLisible("activite", choix.activite, lang) : null, null, null],
                [t.creee, dateDebut ? valeurLisible("dateDebut", dateDebut, lang) : null, ageDe(dateDebut, aujourdhui, t), null],
            ].filter(([, v]) => v)
          : [];

    const rouvrir = (champ) => {
        setChoix((c) => ({ ...c, [champ]: null }));
        onRevoir();
    };

    const puce = (actif, libelle, onClick) => (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={actif}
            className={`${PUCE} ${
                actif
                    ? "border-[#D04515] bg-[#fff4ee] text-[#B83C12] dark:bg-[#D04515]/15 dark:text-[#FF8A5B]"
                    : "border-gray-300 bg-white text-gray-900 hover:border-[#D04515] hover:bg-[#fff8f4] dark:border-white/20 dark:bg-transparent dark:text-white dark:hover:bg-white/5"
            }`}
        >
            {libelle}
        </button>
    );

    const question = (cle, titre, contenu, delai, note) => (
        <motion.div key={cle} className={entreprise ? "mt-4 border-t border-gray-100 dark:border-white/10 pt-3.5" : ""} {...fondu(delai)}>
            <div className="flex flex-wrap items-center gap-2">
                <span className="mr-1 text-sm font-semibold text-gray-900 dark:text-white">{titre}</span>
                {contenu}
            </div>
            {note && <p className="mt-1.5 mb-0 text-xs text-gray-500 dark:text-lightBlue/70">{note}</p>}
        </motion.div>
    );

    const questionStatut = etape === "trouvee" && entreprise?.prisEnCharge && statutAChoisir;
    // Pour un projet, les activités restent à l'écran : on en change sans détour.
    const questionActivite = couverte && activiteAChoisir && (projet || etape === "trouvee");

    return (
        <motion.div
            layoutId={reduit ? undefined : "situation-fiche"}
            transition={{ duration: 0.3, ease: COURBE }}
            style={{ borderRadius: 16 }}
            className="border border-gray-300 dark:border-white/20 bg-white dark:bg-white/[0.05] px-4 py-4 sm:px-5 shadow-[0_10px_30px_rgba(17,24,39,0.05)]"
        >
            {entreprise && (
                <motion.div className="flex items-start gap-3" {...fondu(0.12)}>
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="font-bold text-gray-900 dark:text-white">{entreprise.nom}</span>
                            <span title={t.verifiee} className="inline-flex shrink-0">
                                <SolarIcon name="verified-check-bold" className="h-[18px] w-[18px] text-green-600 dark:text-green-400" />
                                <span className="sr-only">{t.verifiee}</span>
                            </span>
                            {(entreprise.marques || []).map((m) => (
                                <span key={m} className="rounded-full bg-blue-50 dark:bg-blue-500/10 px-2 py-0.5 text-xs font-semibold text-blue-800 dark:text-blue-200">
                                    {t.marques[m]}
                                </span>
                            ))}
                        </div>
                        {entreprise.nomCommercial && <div className="text-sm text-gray-500 dark:text-lightBlue/70">{entreprise.nomCommercial}</div>}
                    </div>
                    <button type="button" onClick={onAutre} className={`${LIEN} shrink-0 text-sm`}>
                        {libelleAutre}
                    </button>
                </motion.div>
            )}

            {infos.length > 0 && (
                <dl className="m-0 mt-3.5 grid grid-cols-2 gap-x-5 gap-y-3 sm:grid-cols-3">
                    {infos.map(([cle, v, precision, champ], i) => (
                        <motion.div key={cle} className="min-w-0" {...fondu(0.2 + i * 0.05)}>
                            <dt className={ETIQUETTE}>{cle}</dt>
                            <dd className="m-0 text-sm font-semibold text-gray-900 dark:text-white">
                                {champ && etape === "curseur" ? (
                                    <button type="button" onClick={() => rouvrir(champ)} title={t.modifier} className="text-left underline decoration-dotted decoration-gray-400 underline-offset-4 hover:decoration-[#D04515]">
                                        {v}
                                    </button>
                                ) : (
                                    v
                                )}
                            </dd>
                            {precision && <dd className="m-0 text-xs text-gray-500 dark:text-lightBlue/70">{precision}</dd>}
                        </motion.div>
                    ))}
                </dl>
            )}

            {entreprise && !couverte && (
                <motion.div className="mt-4 flex flex-col gap-3 rounded-xl bg-blue-50 dark:bg-blue-500/10 px-4 py-3 sm:flex-row sm:items-center" {...fondu(0.3)}>
                    <p className="mb-0 flex-1 text-sm text-blue-900 dark:text-blue-200">{t.nonCouverte}</p>
                    <Link href="/tools/my-situation" className={`${LIEN} shrink-0 text-sm`}>
                        {t.questions}
                    </Link>
                </motion.div>
            )}

            {questionStatut &&
                (entreprise.sarl
                    ? question(
                          "sarl",
                          t.seulAssocie,
                          <>
                              {puce(choix.statut === "eurl", t.seul, () => setChoix((c) => ({ ...c, statut: "eurl" })))}
                              {puce(choix.statut === "plusieurs", t.plusieurs, () => setChoix((c) => ({ ...c, statut: "plusieurs" })))}
                          </>,
                          0.45,
                      )
                    : question(
                          "statut",
                          t.microOuReel,
                          <>
                              {puce(choix.statut === "micro", t.micro, () => setChoix((c) => ({ ...c, statut: "micro" })))}
                              {puce(choix.statut === "reel", t.reel, () => setChoix((c) => ({ ...c, statut: "reel" })))}
                          </>,
                          0.45,
                          t.microDetail,
                      ))}

            {questionActivite &&
                question(
                    "activite",
                    projet ? t.quelleActiviteProjet : t.quelleActivite,
                    // Des exemples de métiers sous chaque catégorie : « libérale »
                    // ou « prestations » ne disent rien à qui se lance.
                    <div className="mt-1 grid basis-full gap-2 sm:grid-cols-2">
                        {["bnc", "services", "vente", "cipav"].map((a) => {
                            const actif = choix.activite === a;
                            return (
                                <button
                                    key={a}
                                    type="button"
                                    onClick={() => setChoix((c) => ({ ...c, activite: a }))}
                                    aria-pressed={actif}
                                    className={`flex min-h-[44px] flex-col items-start rounded-xl border px-3.5 py-2.5 text-left transition-colors duration-200 ${
                                        actif
                                            ? "border-[#D04515] bg-[#fff4ee] dark:bg-[#D04515]/15"
                                            : "border-gray-300 bg-white hover:border-[#D04515] hover:bg-[#fff8f4] dark:border-white/20 dark:bg-transparent dark:hover:bg-white/5"
                                    }`}
                                >
                                    <span className={`text-[15px] font-semibold ${actif ? "text-[#B83C12] dark:text-[#FF8A5B]" : "text-gray-900 dark:text-white"}`}>{t.activites[a]}</span>
                                    <span className="mt-0.5 text-[13px] leading-snug text-gray-500 dark:text-lightBlue/70">{t.activitesExemples[a]}</span>
                                </button>
                            );
                        })}
                    </div>,
                    projet ? 0.1 : questionStatut ? 0.55 : 0.3,
                )}

            {etape === "curseur" && (
                <motion.div className="mt-4 border-t border-gray-100 dark:border-white/10 pt-3.5" {...fondu(0)}>
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <label htmlFor="situation-ca-texte" className="text-base font-semibold text-gray-900 dark:text-white">
                            {projet ? t.encaisseProjet : t.encaisse}
                        </label>
                        <span className="inline-flex items-baseline border-b-2 border-[#D04515]">
                            <input
                                id="situation-ca-texte"
                                inputMode="numeric"
                                value={format.format(ca)}
                                onChange={(e) => {
                                    const n = Number(e.target.value.replace(/\D/g, ""));
                                    if (Number.isFinite(n)) setCa(Math.min(n, 10000000));
                                }}
                                className="w-32 bg-transparent text-center text-2xl font-bold tabular-nums font-sourceSans text-gray-900 dark:text-white focus:outline-hidden"
                            />
                            <span className="pr-1 text-xl font-bold text-gray-900 dark:text-white">€</span>
                        </span>
                        <span className="text-base font-semibold text-gray-900 dark:text-white">{t.parAnHt}</span>
                        <span className="text-[13px] text-gray-500 dark:text-lightBlue/70 sm:ml-auto">{t.glisser}</span>
                    </div>
                    <input
                        type="range"
                        min={0}
                        max={max}
                        step={500}
                        value={Math.min(ca, max)}
                        onChange={(e) => setCa(Number(e.target.value))}
                        aria-label={t.caLibelle}
                        aria-valuetext={euros(ca, lang)}
                        className="mt-3 h-2 w-full cursor-pointer accent-[#D04515]"
                    />
                    {caDepose && <p className="mt-1 mb-0 text-xs text-gray-500 dark:text-lightBlue/70">{remplir(t.caPublie, { annee: caDepose })}</p>}

                    {(infoAcre || tvaProposee) && (
                        <motion.div className="mt-4 flex flex-col gap-1" {...fondu(0.15)}>
                            {infoAcre && (
                                <Interrupteur
                                    actif={acre}
                                    onChange={() => setAcre((a) => !a)}
                                    titre={projet ? t.acreProjet : t.acre}
                                    detail={
                                        <>
                                            {projet
                                                ? remplir(t.acreJusquaProjet, {
                                                      debut: !debutAcre || debutAcre === aujourdhui ? t.aujourdhuiMot : remplir(t.leJour, { date: jourDit(debutAcre) }),
                                                      date: jourDit(infoAcre.fin),
                                                  })
                                                : remplir(t.acreJusqua, { date: dateCourte(infoAcre.fin, lang, annee) })}{" "}
                                            {acre && infoAcre.economie > 0
                                                ? remplir(t.acreGain, { montant: euros(Math.round(infoAcre.economie), lang) })
                                                : !acre && !projet && infoAcre.joursDemande >= 0
                                                  ? remplir(t.acreDelai, { jours: nJours(infoAcre.joursDemande, lang) })
                                                  : null}
                                        </>
                                    }
                                />
                            )}
                            {astuceAcre && (
                                <p className="mb-1 ml-14 flex gap-1.5 text-[13px] leading-snug text-blue-800 dark:text-blue-300">
                                    <SolarIcon name="lightbulb-minimalistic-linear" className="mt-px h-4 w-4 shrink-0" />
                                    <span>
                                        {remplir(t.acreAstuce, { debut: jourDit(astuceAcre.debut), fin: jourDit(astuceAcre.fin), jours: nJours(astuceAcre.attente, lang) })}
                                        {/* Le 1er janvier change aussi d'année de création, donc de première CFE (CGI, art. 1478-II). */}
                                        {astuceAcre.debut.endsWith("-01-01") && ` ${t.acreAstuceCfe}`}
                                    </span>
                                </p>
                            )}
                            {tvaProposee && <Interrupteur actif={tvaFacturee} onChange={() => setTvaFacturee((v) => !v)} titre={t.tvaFacturee} detail={t.tvaFactureeDetail} />}
                        </motion.div>
                    )}
                </motion.div>
            )}
        </motion.div>
    );
}
